import {
  ActionIcon,
  AppShell,
  Box,
  Burger,
  Group,
  Image,
  ScrollArea,
  Skeleton,
  Stack,
  Tooltip,
} from "@mantine/core";
import { useDisclosure, useLocalStorage } from "@mantine/hooks";
import { ModalsProvider } from "@mantine/modals";
import {
  IconActivity,
  IconHierarchy,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconListCheck,
  IconNotes,
  IconPencil,
  IconRobot,
  IconStack2,
  IconTopologyStar2,
} from "@tabler/icons-react";
import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { LayoutProps } from ".";
import { useAuthContext } from "../../auth/useAuthContext";
import { LocalStorageKeys } from "../../utils/cacheKeys";
import { useFeatureAccess } from "../../utils/useFeatureAccess";
import { useProjects } from "../../utils/useProjects";
import { ZedIndex } from "../../utils/zedIndex";
import { Can } from "../auth/Can";
import { NoanLogo } from "../icons/NoanLogo.svg";
import { NavLink } from "../NavLink";
import classes from "./Layout.module.css";
import { PlanUsageBanner } from "./PlanUsageBanner";
import { ProjectSwitcher } from "./ProjectSwitcher";
import { TrialBanner } from "./TrialBanner";
import { UserMenu } from "./UserMenu";

export function SidebarLayout({ topHeader }: Pick<LayoutProps, "topHeader">) {
  const location = useLocation();

  /**
   * Collapse/hide navigation + mobile behavior
   */
  const [navOpened, { toggle: toggleNav, close: closeNav }] = useDisclosure();
  const [navCollapsed, setNavCollapsed] = useLocalStorage({
    key: LocalStorageKeys.NavbarCollapsed,
    defaultValue: false,
  });

  const {
    isRestrictedRole,
    isCreateModeEnabled,
    isBuildModeEnabled,
    isNetworkEnabled,
    isNotesEnabled,
    isTasksEnabled,
  } = useFeatureAccess();

  /**
   * Account information + org switcher
   */
  const { identity, isPayingCustomer } = useAuthContext();
  const { projects, isLoadingProjects } = useProjects();

  const activeProject = projects.find((o) => o.id === identity.organizationId);

  const orgLogo = activeProject?.logoUrl;

  /**
   * Ensure the mobile nav gets toggled off when navigating elsewhere. This is important
   * if we're on mobile, otherwise the nav is over the content and you can't tell
   * we've navigated elsewhere
   */
  useEffect(() => {
    closeNav();
  }, [location, closeNav]);

  return (
    <ModalsProvider
      modalProps={{
        withinPortal: true,
      }}
    >
      <AppShell
        layout="default"
        header={{ height: { base: 60, sm: 0 } }}
        navbar={{
          width: { base: navCollapsed ? 100 : 256 },
          breakpoint: "sm",
          collapsed: { mobile: !navOpened },
        }}
        padding={{
          base: 0,
        }}
        classNames={{
          root: classes.appShell,
          main: classes.appMain,
        }}
      >
        <AppShell.Navbar
          className={classes.sidebarNavbar}
          zIndex={navOpened ? ZedIndex.Sidebar : undefined}
        >
          {/* Desktop Logo */}
          <AppShell.Section>
            <Group className={classes.sidebarExpandButtonContainer}>
              <ActionIcon
                variant="default"
                onClick={() => setNavCollapsed(!navCollapsed)}
                size="lg"
                className={classes.sidebarExpandButton}
              >
                {navCollapsed ? (
                  <IconLayoutSidebarLeftExpand />
                ) : (
                  <IconLayoutSidebarLeftCollapse />
                )}
              </ActionIcon>
            </Group>

            <Stack
              align="center"
              gap="md"
              justify="space-between"
              className={classes.sidebarLayoutLogo}
              visibleFrom="sm"
            >
              {isLoadingProjects ? (
                <Skeleton w={80} h={80} />
              ) : orgLogo ? (
                <Image
                  src={orgLogo}
                  mah={80}
                  w="fit-content"
                  maw="100%"
                  fit="contain"
                  radius="sm"
                />
              ) : (
                <NoanLogo />
              )}
            </Stack>
          </AppShell.Section>

          <AppShell.Section grow component={ScrollArea}>
            <ProjectSwitcher />
            <TrialBanner navCollapsed={navCollapsed} />
            <PlanUsageBanner navCollapsed={navCollapsed} />
            <Stack mt="lg" flex={1} gap={3} w="100%">
              <Tooltip
                label={isRestrictedRole ? "Dashboard" : "Activity"}
                disabled={!navCollapsed}
                position="right"
              >
                <NavLink
                  to="/activity"
                  icon={<IconActivity />}
                  label={isRestrictedRole ? "Dashboard" : "Activity"}
                />
              </Tooltip>
              {isBuildModeEnabled && (
                <Can execute="graph.read">
                  <Tooltip
                    label="Graph"
                    disabled={!navCollapsed}
                    position="right"
                  >
                    <NavLink
                      to="/project-graph"
                      icon={<IconHierarchy />}
                      label="Graph"
                    />
                  </Tooltip>
                </Can>
              )}
              {isBuildModeEnabled && (
                <Can execute="fact.read">
                  <Tooltip
                    label="Facts"
                    disabled={!navCollapsed}
                    position="right"
                  >
                    <NavLink to="/build" icon={<IconStack2 />} label="Facts" />
                  </Tooltip>
                </Can>
              )}
              {isCreateModeEnabled && (
                <Can execute="asset.read">
                  <Tooltip
                    label="Assets"
                    disabled={!navCollapsed}
                    position="right"
                  >
                    <NavLink
                      to="/create"
                      icon={<IconPencil />}
                      label="Assets"
                    />
                  </Tooltip>
                </Can>
              )}
              {isTasksEnabled && (
                <Tooltip
                  label="Tasks"
                  disabled={!navCollapsed}
                  position="right"
                >
                  <NavLink to="/tasks" icon={<IconListCheck />} label="Tasks" />
                </Tooltip>
              )}
              {isNotesEnabled && (
                <Can execute="note.read">
                  <Tooltip
                    label="Notes"
                    disabled={!navCollapsed}
                    position="right"
                  >
                    <NavLink to="/ideas" icon={<IconNotes />} label="Notes" />
                  </Tooltip>
                </Can>
              )}
              {isNetworkEnabled && (
                <Can execute="network.read">
                  <Tooltip
                    label="Network"
                    disabled={!navCollapsed}
                    position="right"
                  >
                    <NavLink
                      to="/network"
                      icon={<IconTopologyStar2 />}
                      label="Network"
                    />
                  </Tooltip>
                </Can>
              )}
              <Tooltip label="Agents" disabled={!navCollapsed} position="right">
                <NavLink to="/agents" icon={<IconRobot />} label="Agents" />
              </Tooltip>
            </Stack>
          </AppShell.Section>

          <Group
            align="center"
            justify="space-between"
            className={classes.accountButtonContainer}
          >
            <UserMenu variant="sidebar" withReferral={isPayingCustomer} />
          </Group>
        </AppShell.Navbar>
        <AppShell.Header hiddenFrom="sm">
          {/* Mobile Header + Logo */}
          <Group w="100%" h="100%" px="md" justify="space-between">
            {isLoadingProjects ? (
              <Skeleton w={35} h={35} />
            ) : orgLogo ? (
              <Image src={orgLogo} className={classes.sidebarOrgLogo} />
            ) : (
              <NoanLogo width={90} />
            )}
            <Burger opened={navOpened} onClick={toggleNav} />
          </Group>
        </AppShell.Header>
        <AppShell.Main>
          {topHeader}
          <Box className={classes.outletContainer}>
            <Outlet />
          </Box>
        </AppShell.Main>
      </AppShell>
    </ModalsProvider>
  );
}
